import React from 'react';
import ReactDOM from 'react-dom';
import MarkupMenu from './components/markup-menu';
import NameTextSnippetEditor from './components/name-text-snippet-editor';
import useNameEditorPositionStyles from 'components/text-editor/components/controls/hooks/use-name-editor-position-styles';
import useControlPositionStyles from 'components/text-editor/components/controls/hooks/use-control-position-styles';
import useMarkupMenuPositionStyles from 'components/text-editor/components/controls/hooks/use-markup-menu-position-styles';
import useMarkupMenuOperations from './hooks/use-markup-menu-operations';
import useCustomEditor from 'components/text-editor/hooks/use-custom-editor';
import { Container, MarkupMenuContainer, NameEditorContainer } from './style';
import { isFunction } from 'lodash';
import { notNull } from 'utils/js';
import { AnnotationData } from 'types';
import { Range } from 'slate';

interface ControlProps {
  showFinishLinkAction?: boolean;
  showDeleteLinkAction?: (data: AnnotationData) => boolean;
  onTextSnippetSave?: (data: AnnotationData) => void;
  onTextSnippetDelete?: (data: AnnotationData) => void;
  onDeleteLink?: (data: AnnotationData) => void;
  onStartLinking?: (data: AnnotationData) => void;
  onFinishLinking?: (data: AnnotationData) => void;
  onGlobalLinkIndicatorClick?: (data: AnnotationData) => void;
  renderedAt?: string;
}

/**
 * Controls handles the positioning and toggling of text markup elements, such as toolbars and snippet name editors.
 */
export default function Controls(props: ControlProps) {
  const editor = useCustomEditor();

  const {
    name,
    isNameEditorVisible,
    isNameDisplayVisible,
    showNameEditor,
    hideNameEditor,
    saveTextSnippet,
    deleteTextSnippet,
    startLinking,
    finishLinking,
    deleteLink,
    getSelectedAnnotationData,
  } = useMarkupMenuOperations(props);

  const showDeleteLinkAction = isFunction(props.showDeleteLinkAction)
    ? props.showDeleteLinkAction(getSelectedAnnotationData())
    : props.showDeleteLinkAction;

  const controlPositionStyles = useControlPositionStyles();
  const markupMenuPositionStyles = useMarkupMenuPositionStyles();
  const nameEditorPositionStyles = useNameEditorPositionStyles();

  const textHasSelection =
    (notNull(editor.selection) &&
      Range.isExpanded(editor.selection as Range)) ||
    Boolean(editor.getSelectedNode());

  const isMarkupMenuVisible = textHasSelection || isNameDisplayVisible;

  const isNameButtonDisabled = textHasSelection === false;

  const isLinkButtonDisabled = textHasSelection === false;

  const portalElement = (
    <Container style={controlPositionStyles}>
      <NameEditorContainer style={nameEditorPositionStyles}>
        <NameTextSnippetEditor
          name={name}
          isNameDisplayVisible={isNameDisplayVisible}
          isNameEditorVisible={isNameEditorVisible}
          showNameEditor={showNameEditor}
          hideNameEditor={hideNameEditor}
          saveTextSnippet={saveTextSnippet}
          deleteTextSnippet={deleteTextSnippet}
        />
      </NameEditorContainer>
      <MarkupMenuContainer style={markupMenuPositionStyles}>
        {isMarkupMenuVisible && (
          <MarkupMenu
            showNameEditor={showNameEditor}
            isNameButtonDisabled={isNameButtonDisabled}
            isLinkButtonDisabled={isLinkButtonDisabled}
            isNameEditorVisible={isNameEditorVisible}
            isNameDisplayVisible={isNameDisplayVisible}
            showFinishLinkAction={props.showFinishLinkAction}
            showDeleteLinkAction={showDeleteLinkAction}
            startLinking={startLinking}
            finishLinking={finishLinking}
            deleteLink={deleteLink}
          />
        )}
      </MarkupMenuContainer>
    </Container>
  );

  return ReactDOM.createPortal(portalElement, document.body);
}
